import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Box, Grid } from '@mui/material';
import { Company } from '../../models/Company';
import { Requirement } from '../../models/Requirement';
import { Profile } from '../../models/Profile';
import { companyService } from '../../services/companyService';
import { requirementService } from '../../services/requirementService';
import { profileService } from '../../services/profileService';
import { showAlert } from '../../utils/alert';
import { isValidEmail } from '../../utils/validation';
import PageHeader from '../../components/PageHeader';

import CompanyCard from './components/CompanyCard';
import RequirementsList from './components/RequirementsList';
import ProfileDashboard from './components/ProfileDashboard';
import AddProfileDialog from './components/AddProfileDialog';

const ProcessProfiles: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience_years: '',
    current_location: '',
    preferred_location: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    highest_education: '',
    current_employer: '',
    offer_in_hand: false,
    profile_file: null as File | null
  });
  const [errors, setErrors] = useState<{[key: string]: boolean | string}>({});
  const [submitting, setSubmitting] = useState(false);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    const processedValue = name === 'offer_in_hand' ? value === 'Yes' : value;
    setFormData(prev => ({...prev, [name]: processedValue}));
  };

  const handleFileChange = (file: File | null) => {
    setFormData(prev => ({...prev, profile_file: file}));
    
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({...prev, profile_file: 'Only PDF and Word documents are allowed'}));
        return;
      }
      
      if (file.size > maxSize) {
        setErrors(prev => ({...prev, profile_file: 'File size must be less than 5MB'}));
        return;
      }
      
      setErrors(prev => ({...prev, profile_file: false}));
    } else {
      setErrors(prev => ({...prev, profile_file: false}));
    }
  };

  const handleBlur = (field: string) => {
    const value = formData[field as keyof typeof formData];
    if (typeof value === 'string' && value.trim()) {
      if (field === 'email' && !isValidEmail(value)) {
        setErrors(prev => ({...prev, [field]: 'Please enter a valid email address'}));
        return;
      }
      setErrors(prev => ({...prev, [field]: false}));
    }
  };

  const handleAddProfile = async () => {
    if (!selectedRequirement) {
      showAlert('Please select a requirement first', 'error');
      return;
    }

    // Validate all required fields
    const requiredFields = ['name', 'email', 'phone', 'skills', 'experience_years', 'current_location', 'preferred_location', 'current_ctc', 'expected_ctc', 'notice_period', 'highest_education', 'current_employer'];
    const newErrors: {[key: string]: boolean | string} = {};
    
    requiredFields.forEach(field => {
      const value = formData[field as keyof typeof formData];
      if (typeof value === 'string' && !value.trim()) {
        newErrors[field] = true;
      } else if (field === 'email' && typeof value === 'string' && !isValidEmail(value)) {
        newErrors[field] = 'Please enter a valid email address';
      }
    });
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    setSubmitting(true);
    try {
      const payload: Omit<Profile, 'id' | 'created_date' | 'updated_date'> & { requirement_id: number } = {
        ...formData,
        experience_years: parseInt(formData.experience_years) || 0,
        current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
        expected_ctc: formData.expected_ctc ? parseFloat(formData.expected_ctc) : null,
        notice_period: formData.notice_period || null,
        highest_education: formData.highest_education || null,
        current_employer: formData.current_employer || null,
        offer_in_hand: formData.offer_in_hand,
        requirement_id: selectedRequirement.requirement_id
      };
      
      const response = await profileService.createProfile(payload);
      
      if (response.success) {
        showAlert('Candidate added successfully', 'success');
        setShowAddForm(false);
        setFormData({
          name: '', email: '', phone: '', skills: '', experience_years: '',
          current_location: '', preferred_location: '', current_ctc: '',
          expected_ctc: '', notice_period: '', highest_education: '',
          current_employer: '', offer_in_hand: false, profile_file: null
        });
        setErrors({});
        setProfileRefreshKey(prev => prev + 1);
      } else {
        showAlert('Failed to add candidate', 'error');
      }
    } catch (error) {
      showAlert('Error adding candidate', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    setSelectedRequirement(null);
    if (selectedCompanyId) {
      loadRequirements();
    } else {
      setRequirements([]);
    }
  }, [selectedCompanyId]);

  const getTopCompanies = (companies: Company[]): Company[] => {
    // Dummy logic - will be replaced later
    return companies.slice(0, 3);
  };

  const loadCompanies = async () => {
    try {
      const response = await companyService.getActiveCompanies();
      if (response.success && response.data) {
        setCompanies(getTopCompanies(response.data));
      }
    } catch (error) {
      showAlert('Failed to load companies', 'error');
    }
  };

  const loadRequirements = async () => {
    setLoading(true);
    try {
      const response = await requirementService.getOpenRequirements(selectedCompanyId as number);
      if (response.success && response.data) {
        setRequirements(response.data);
      } else {
        setRequirements([]);
        setSelectedRequirement(null);
      }
    } catch (error) {
      showAlert('Failed to load requirements', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <PageHeader title="Recruiter's Dashboard" />
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
            🏢 Companies
          </Typography>
          <Grid container spacing={3}>
            {companies.map((company) => (
              <Grid item xs={12} md={4} key={company.id}>
                <CompanyCard
                  company={company}
                  isSelected={selectedCompanyId === company.id}
                  onSelect={setSelectedCompanyId}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {selectedCompanyId && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <RequirementsList
                requirements={requirements}
                selectedRequirement={selectedRequirement}
                onSelectRequirement={setSelectedRequirement}
                loading={loading}
                companyName={companies.find(c => c.id === selectedCompanyId)?.name}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <ProfileDashboard
                key={`${selectedRequirement?.requirement_id}-${profileRefreshKey}`}
                selectedRequirement={selectedRequirement}
                onAddProfile={() => setShowAddForm(true)}
              />
            </Grid>
          </Grid>
        )}
      </Paper>
      
      <AddProfileDialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        formData={formData}
        errors={errors}
        submitting={submitting}
        onInputChange={handleInputChange}
        onFileChange={handleFileChange}
        onBlur={handleBlur}
        onSubmit={handleAddProfile}
      />
    </Container>
  );
};

export default ProcessProfiles;